import { type CreditCardVendorEnum } from '@webapp/core/entities/external/CreditCardVendor'
import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import {
  CreditCardAssignee,
  type CreditCardAssigneeConstructor,
} from '../CreditCardAssignee'
import { ReconcileSessionStatuses } from '../enums/ReconcileSessionStatusEnum'
import {
  ReconcileSession,
  type ReconcileSessionConstructor,
} from '../ReconcileSession'

// Minimal vendor reference — provider display + payment-method fetch on the
// bill page. Local value object, not a standalone entity.
interface CreditCardVendorRef {
  id: number
  name: string
}

export interface CreditCardAccountConstructor {
  id: number
  name: string
  number: string // raw card number suffix from API; masked for display
  type: CreditCardVendorEnum
  currency: CurrencyConstructor
  vendor: CreditCardVendorRef
  glCode: string
  assignees: CreditCardAssigneeConstructor[]
  lastReconciliationDate: Nullable<ISODateString>
  lastReconciliationBalance: Nullable<number>
  lastStatementDate: Nullable<ISODateString>
  session: Nullable<ReconcileSessionConstructor>
}

export class CreditCardAccount {
  readonly id: number
  readonly name: string
  readonly number: string
  readonly type: CreditCardVendorEnum
  readonly currency: Currency
  readonly vendor: CreditCardVendorRef
  readonly glCode: string
  readonly assignees: CreditCardAssignee[]
  readonly lastReconciliationDate: Nullable<Date>
  readonly lastReconciliationBalance: Nullable<number>
  readonly lastStatementDate: Nullable<Date>
  readonly session: Nullable<ReconcileSession>

  constructor(data: CreditCardAccountConstructor) {
    this.id = data.id
    this.name = data.name
    this.number = data.number
    this.type = data.type
    this.currency = new Currency(data.currency)
    this.vendor = data.vendor
    this.glCode = data.glCode
    this.assignees = data.assignees.map(
      (assignee) => new CreditCardAssignee(assignee)
    )
    this.lastReconciliationDate = data.lastReconciliationDate
      ? new Date(data.lastReconciliationDate)
      : null
    this.lastReconciliationBalance = data.lastReconciliationBalance
    this.lastStatementDate = data.lastStatementDate
      ? new Date(data.lastStatementDate)
      : null
    this.session = data.session ? new ReconcileSession(data.session) : null
  }

  // "•••• 5978" — client masks the raw number (BHV-001)
  get maskedNumber(): string {
    return `•••• ${this.number.slice(-4)}`
  }

  // No session, or session APPROVED (CreditCardStatus.CONFIRMED) → green
  // "Ready to Reconcile" (BHV-003)
  get isReadyToReconcile(): boolean {
    return (
      this.session === null ||
      this.session.status === ReconcileSessionStatuses.APPROVED
    )
  }

  // Current Statement Date is nulled for display when confirmed (BHV-003)
  get currentStatementDate(): Nullable<Date> {
    return this.isReadyToReconcile ? null : this.lastStatementDate
  }

  // Assigned-user count on settings list rows (BHV-009)
  get assigneeCount(): number {
    return this.assignees.length
  }

  get hasDraftSession(): boolean {
    return this.session?.status === ReconcileSessionStatuses.DRAFT
  }

  get hasSubmittedSession(): boolean {
    return this.session?.status === ReconcileSessionStatuses.SUBMITTED
  }

  // Row lock: locked by someone other than the current user (BHV-002/007)
  isLockedByOther(currentUserId: number): boolean {
    return this.session?.lockStatus.isLockedByOther(currentUserId) ?? false
  }
}
